import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const LanguageContext = createContext();

const translations = {
  en: {
    backToInvitation: "← Back to Invitation",
    photoAlbums: "Photo Albums",
    browseMemories: "Browse our wedding memories",
    noPhotos: "No photos",
    photosCount: (count) => `${count} photo${count !== 1 ? 's' : ''}`,
    backToAlbums: "← Back to Albums",
    photoGallery: "Photo Gallery",
    uploading: "Uploading...",
    uploadPhotos: "+ Upload Photos/Videos",
    loadingPhotos: "Loading photos...",
    allPhotosLoaded: (count) => `All ${count} photos loaded`,
    noPhotosYet: "No photos yet",
    scrollForMore: "Scroll for more...",
    welcome: "Welcome",
    logout: "Logout",
    rsvp: "RSVP",
    submittedOn: (date) => `Submitted on ${date}`,
    saving: "Saving...",
    submitRsvp: "Submit RSVP",
    attending: "Attending?",
    yes: "Yes",
    no: "No",
    yesRsvp: "Yes, I'll be there",
    noRsvp: "Sorry, I can't make it",
    dietary: "Dietary Requirements",
    dietaryPlaceholder: "Any dietary restrictions or allergies?",
    extraNotes: "Extra Notes",
    notesPlaceholder: "Any special requests or information we should know?",
    bringingPlusOne: "Bringing a plus one",
    rsvpSuccess: "RSVP submitted successfully!",
    rsvpError: "Failed to submit RSVP. Please try again.",
    yourAlbum: "Your Album",
    viewAndUpload: "View and upload photos to your private album",
    viewAlbum: "View Album",
    publicGallery: "Public Gallery",
    viewPhotosAll: "View photos from all guests",
    viewGallery: "View Gallery",
    loginToRsvp: "Login to RSVP",
    loggingIn: "Logging in...",
    accessCode: "Access Code *",
    accessCodePlaceholder: "Enter your access code",
    pleaseLogin: "Please log in with your access code to RSVP",
    home: "Home",
    gallery: "Gallery",
    guestLogin: "Guest Login",
    selectEvents: "Which events will you attend?",
    accommodation: "Accomodation",
    invalidCode: "Invalid access code",
    waxSealAlt: "Gold Wax Seal Monogram",
    clickToOpen: "Click seal to open",
    saveTheDate: "Save the Date",
    continue: "Continue",
    guestLoginDesc: "Enter your access code to view your RSVP and album",
    backToHome: "Back to Home",
    login: "Login",
    accessCodeLabel: "Access Code",
  },
  lt: {
    backToInvitation: "← Atgal į kvietimą",
    photoAlbums: "Photo Albums",
    browseMemories: "Naršykite mūsų vestuvių akimirkas",
    noPhotos: "Nuotraukų nėra",
    photosCount: (count) => `${count} nuotrauka${count !== 1 ? 'os' : 'ą'}`,
    backToAlbums: "← Atgal į albumus",
    photoGallery: "Photo Gallery",
    uploading: "Įkeliama...",
    uploadPhotos: "+ Įkelti nuotraukas / vaizdo įrašus",
    loadingPhotos: "Kraunamos nuotraukos...",
    allPhotosLoaded: (count) => `Įkeltos visos ${count} nuotraukos`,
    noPhotosYet: "Nuotraukų dar nėra",
    scrollForMore: "Slinkite žemyn, kad pamatytumėte daugiau...",
    welcome: "Sveiki",
    logout: "Atsijungti",
    rsvp: "RSVP",
    submittedOn: (date) => `Pateikta ${date}`,
    saving: "Išsaugoma...",
    submitRsvp: "Pateikti atsakymą",
    attending: "Ar dalyvausite?",
    yes: "Taip",
    no: "Ne",
    yesRsvp: "Taip, aš dalyvausiu",
    noRsvp: "Atsiprašau, negalėsiu atvykti",
    dietary: "Dietiniai reikalavimai",
    dietaryPlaceholder: "Turite kokių nors mitybos apribojimų ar alergijų?",
    extraNotes: "Papildomos pastabos",
    notesPlaceholder: "Turite kokių nors specialių pageidavimų ar informacijos, kurią turėtume žinoti?",
    bringingPlusOne: "Kartu atvyksiu su pora",
    rsvpSuccess: "Atsakymas sėkmingai išsiųstas!",
    rsvpError: "Nepavyko pateikti atsakymo. Bandykite dar kartą.",
    yourAlbum: "Your Album",
    viewAndUpload: "Peržiūrėkite ir įkelkite nuotraukas į savo privatų albumą",
    viewAlbum: "Peržiūrėti albumą",
    publicGallery: "Public Gallery",
    viewPhotosAll: "Peržiūrėkite visų svečių nuotraukas",
    viewGallery: "Žiūrėti galeriją",
    loginToRsvp: "Prisijungti",
    loggingIn: "Prisijungiama...",
    accessCode: "Prisijungimo kodas *",
    accessCodePlaceholder: "Įveskite savo prisijungimo kodą",
    pleaseLogin: "Įveskite prisijungimo kodą, kad užpildytumėte RSVP",
    home: "Pagrindinis",
    gallery: "Galerija",
    guestLogin: "Guest Login",
    selectEvents: "Kuriose dalyse dalyvausite?",
    accommodation: "Apgyvendinimas",
    invalidCode: "Neteisingas prisijungimo kodas",
    waxSealAlt: "Auksinis vaško antspaudas",
    clickToOpen: "Spustelėkite antspaudą, kad atidarytumėte",
    saveTheDate: "Išsaugokite datą",
    continue: "Tęsti",
    guestLoginDesc: "Įveskite prisijungimo kodą, kad peržiūrėtumėte savo RSVP ir albumą",
    backToHome: "Atgal į pagrindinį",
    login: "Prisijungti",
    accessCodeLabel: "Prisijungimo kodas",
  }
};

export function LanguageProvider({ children }) {
  const location = useLocation();

  const getLangFromUrl = () => {
    const pathname = location.pathname;
    if (pathname.startsWith('/lt/') || pathname === '/lt') {
      return 'lt';
    }
    return 'en';
  };

  const [lang, setLangState] = useState(() => {
    const urlLang = getLangFromUrl();
    if (urlLang) return urlLang;
    
    const savedLang = localStorage.getItem('lang');
    if (savedLang === 'lt' || savedLang === 'en') {
      return savedLang;
    }
    return 'en';
  });

  const setLang = (newLang) => {
    if (newLang !== 'en' && newLang !== 'lt') return;
    setLangState(newLang);
    localStorage.setItem('lang', newLang);
    
    // Update path
    const currentPath = window.location.pathname;
    let newPath = currentPath;
    
    if (newLang === 'lt') {
      if (!currentPath.startsWith('/lt/') && currentPath !== '/lt') {
        newPath = '/lt' + (currentPath === '/' ? '' : currentPath);
      }
    } else { // 'en'
      if (currentPath.startsWith('/lt/') || currentPath === '/lt') {
        newPath = currentPath.substring(3); // Remove '/lt'
        if (newPath === '') newPath = '/';
      }
    }
    
    // Preserve search params and hash
    newPath += window.location.search + window.location.hash;
    window.location.href = newPath; // Reload to ensure clean state
  };

  // Sync state if URL query parameter changes (popstate)
  useEffect(() => {
    const handlePopState = () => {
      const urlLang = getLangFromUrl();
      if (urlLang && urlLang !== lang) {
        setLangState(urlLang);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [lang]);

  // Sync when location search or pathname changes
  useEffect(() => {
    const urlLang = getLangFromUrl();
    if (urlLang && urlLang !== lang) {
      setLangState(urlLang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, location.pathname, location.search]);

  const t = (key, ...args) => {
    const translation = translations[lang]?.[key] || translations['en']?.[key] || key;
    if (typeof translation === 'function') {
      return translation(...args);
    }
    return translation;
  };

  const l = (path) => {
    if (lang === 'lt') {
      if (path === '/') return '/lt';
      if (path.startsWith('/lt/') || path === '/lt') return path;
      return `/lt${path}`;
    }
    return path;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, l }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
